import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiExternalLink,
  FiLayers,
  FiLock,
  FiPlay,
  FiSearch,
  FiShield,
  FiVideo,
  FiX
} = FiIcons;

const READY_MESSAGE = 'BPA_VIDEO_HUB_READY';
const LIBRARY_MESSAGE = 'BPA_VIDEO_HUB_LIBRARY';
const AUTHORIZED_MESSAGE = 'BPA_VIDEO_HUB_AUTHORIZED';
const HEARTBEAT_MESSAGE = 'BPA_VIDEO_HUB_HEARTBEAT';
const ALIVE_MESSAGE = 'BPA_VIDEO_HUB_ALIVE';
const REVOKE_MESSAGE = 'BPA_VIDEO_HUB_REVOKE';
const LAUNCH_TIMEOUT_MS = 30000;
const HEARTBEAT_INTERVAL_MS = 10000;
const HOST_LIVENESS_TIMEOUT_MS = 25000;
const LIBRARY_KEY_PATTERN = /^[A-Za-z0-9]{48}$/;

const createRequestId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();

  const bytes = new Uint8Array(16);
  window.crypto?.getRandomValues?.(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
};

const isSafeHttpUrl = (value) => {
  try {
    const url = new URL(String(value || ''));
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch (_) {
    return false;
  }
};

const sanitizeTags = (value) => Array.isArray(value)
  ? value.map(item => String(item || '').trim()).filter(Boolean).slice(0, 30)
  : [];

const sanitizeVideo = (video) => {
  if (!video || typeof video !== 'object') return null;

  const title = String(video.title || '').trim();
  if (!title) return null;

  return {
    id: String(video.id || title),
    title,
    description: String(video.description || ''),
    video_url: isSafeHttpUrl(video.video_url) ? String(video.video_url) : '',
    thumbnail_url: isSafeHttpUrl(video.thumbnail_url) ? String(video.thumbnail_url) : '',
    category: String(video.category || ''),
    tags: sanitizeTags(video.tags),
    keywords: String(video.keywords || ''),
    duration_seconds: Number.isFinite(Number(video.duration_seconds))
      ? Number(video.duration_seconds)
      : null,
    sort_order: Number.isFinite(Number(video.sort_order)) ? Number(video.sort_order) : 0
  };
};

const sanitizeLibraryPayload = (payload) => {
  if (!payload || typeof payload !== 'object' || !payload.app) return null;

  const appName = String(payload.app.app_name || payload.app.name || '').trim();
  if (!appName) return null;

  const videos = Array.isArray(payload.videos)
    ? payload.videos.map(sanitizeVideo).filter(Boolean)
    : [];

  const videoById = new Map(videos.map(video => [video.id, video]));

  const playlists = Array.isArray(payload.playlists)
    ? payload.playlists.map((playlist) => {
        if (!playlist || typeof playlist !== 'object') return null;
        const name = String(playlist.name || '').trim();
        if (!name) return null;

        const items = Array.isArray(playlist.items)
          ? playlist.items.map((item) => {
              const rawVideo = item?.video || item;
              const sanitized = sanitizeVideo(rawVideo);
              if (!sanitized) return null;

              const video = videoById.get(sanitized.id) || sanitized;
              return {
                id: String(item?.id || `${name}-${video.id}`),
                position: Number.isFinite(Number(item?.position)) ? Number(item.position) : 0,
                video
              };
            }).filter(Boolean).sort((a, b) => a.position - b.position)
          : [];

        return {
          id: String(playlist.id || name),
          name,
          description: String(playlist.description || ''),
          tags: sanitizeTags(playlist.tags),
          items
        };
      }).filter(Boolean)
    : [];

  return {
    app: {
      app_name: appName,
      app_slug: String(payload.app.app_slug || '')
    },
    audienceRole: String(payload.audienceRole || ''),
    videos: [...videos].sort((a, b) => a.sort_order - b.sort_order),
    playlists
  };
};

const normalizeSearchText = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^a-z0-9\s-]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getSearchTokens = (query) => normalizeSearchText(query).split(' ').filter(token => token.length > 1);

const includesEveryToken = (text, tokens) => {
  if (tokens.length === 0) return true;
  const normalized = normalizeSearchText(text);
  return tokens.every(token => normalized.includes(token));
};

const getVideoSearchText = (video, topicNames = []) => [
  video.title,
  video.description,
  video.category,
  video.keywords,
  ...(video.tags || []),
  ...topicNames
].join(' ');

const scoreVideo = (video, query, tokens, topicNames = []) => {
  if (tokens.length === 0) return 1;
  const title = normalizeSearchText(video.title);
  const description = normalizeSearchText(video.description);
  const category = normalizeSearchText(video.category);
  const tags = normalizeSearchText((video.tags || []).join(' '));
  const keywords = normalizeSearchText(video.keywords);
  const topics = normalizeSearchText(topicNames.join(' '));
  const phrase = normalizeSearchText(query);
  let score = title.includes(phrase) ? 14 : 0;

  tokens.forEach(token => {
    if (title.includes(token)) score += 7;
    if (topics.includes(token)) score += 6;
    if (tags.includes(token)) score += 5;
    if (keywords.includes(token)) score += 5;
    if (category.includes(token)) score += 3;
    if (description.includes(token)) score += 2;
  });
  return score;
};

const formatDuration = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return '';
  const minutes = Math.floor(value / 60);
  const remainder = Math.floor(value % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
};

const TrainingLibrary = () => {
  const { libraryKey } = useParams();
  const requestIdRef = useRef(createRequestId());
  const [status, setStatus] = useState('checking');
  const [library, setLibrary] = useState(null);
  const [message, setMessage] = useState('Verifying secure access...');
  const [query, setQuery] = useState('');
  const [activeTopicId, setActiveTopicId] = useState('all');

  useEffect(() => {
    let cancelled = false;
    let launchTimeoutId;
    let heartbeatIntervalId;
    let livenessCheckId;
    let authorizedOrigin = '';
    let lastAliveAt = 0;
    let isAuthorized = false;

    const lockLibrary = (text) => {
      if (cancelled) return;
      isAuthorized = false;
      authorizedOrigin = '';
      lastAliveAt = 0;
      if (heartbeatIntervalId) window.clearInterval(heartbeatIntervalId);
      if (livenessCheckId) window.clearInterval(livenessCheckId);
      setLibrary(null);
      setStatus('denied');
      setMessage(text);
      setQuery('');
      setActiveTopicId('all');
    };

    if (!libraryKey || !LIBRARY_KEY_PATTERN.test(libraryKey)) {
      lockLibrary('This training library link is invalid.');
      return undefined;
    }

    const launchedInFrame = window.parent !== window;
    const launchedFromPopup = !launchedInFrame && window.opener && !window.opener.closed;
    const launchSource = launchedInFrame
      ? window.parent
      : launchedFromPopup
        ? window.opener
        : null;

    if (!launchSource) {
      lockLibrary('For security, this training library must be opened from your signed-in application.');
      return undefined;
    }

    const sendHeartbeat = () => {
      if (!isAuthorized || !authorizedOrigin || cancelled) return;

      try {
        launchSource.postMessage({
          type: HEARTBEAT_MESSAGE,
          requestId: requestIdRef.current,
          libraryKey
        }, authorizedOrigin);
      } catch (error) {
        console.error('Training host heartbeat failed:', error);
        lockLibrary('Your application session could no longer be verified. Reopen Training Videos from the signed-in application.');
      }
    };

    const startLivenessWatch = () => {
      lastAliveAt = Date.now();
      sendHeartbeat();

      heartbeatIntervalId = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
      livenessCheckId = window.setInterval(() => {
        if (!isAuthorized || cancelled) return;
        if (Date.now() - lastAliveAt > HOST_LIVENESS_TIMEOUT_MS) {
          lockLibrary('Your application session is no longer active. Reopen Training Videos after signing in again.');
        }
      }, 5000);
    };

    const handleMessage = (event) => {
      if (event.source !== launchSource) return;

      const payload = event.data;
      if (!payload || typeof payload !== 'object') return;
      if (payload.requestId !== requestIdRef.current) return;
      if (payload.libraryKey && payload.libraryKey !== libraryKey) return;

      if (isAuthorized) {
        if (event.origin !== authorizedOrigin) return;

        if (payload.type === ALIVE_MESSAGE) {
          lastAliveAt = Date.now();
          return;
        }

        if (payload.type === REVOKE_MESSAGE) {
          lockLibrary('Your application session ended. Sign in again to access Training Videos.');
        }
        return;
      }

      if (payload.type !== LIBRARY_MESSAGE) return;

      const sanitizedLibrary = sanitizeLibraryPayload(payload.library);
      if (!sanitizedLibrary) {
        lockLibrary('Training access could not be verified. Reopen Training Videos from your application.');
        return;
      }

      window.clearTimeout(launchTimeoutId);
      authorizedOrigin = event.origin;
      isAuthorized = true;
      setLibrary(sanitizedLibrary);
      setStatus('authorized');
      setMessage('Access verified.');

      launchSource.postMessage({
        type: AUTHORIZED_MESSAGE,
        requestId: requestIdRef.current,
        libraryKey
      }, authorizedOrigin);

      startLivenessWatch();
    };

    window.addEventListener('message', handleMessage);

    setStatus('waiting');
    setMessage('Waiting for secure training data from your signed-in application...');

    // READY contains no credential or training data. Authorised content is sent only
    // after the host validates its own signed-in user through its trusted backend.
    launchSource.postMessage({
      type: READY_MESSAGE,
      requestId: requestIdRef.current,
      libraryKey
    }, '*');

    launchTimeoutId = window.setTimeout(() => {
      lockLibrary('Secure authorization timed out. Return to your application and open Training Videos again.');
    }, LAUNCH_TIMEOUT_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(launchTimeoutId);
      if (heartbeatIntervalId) window.clearInterval(heartbeatIntervalId);
      if (livenessCheckId) window.clearInterval(livenessCheckId);
      window.removeEventListener('message', handleMessage);
    };
  }, [libraryKey]);

  const appName = library?.app?.app_name || 'Training Centre';
  const topics = library?.playlists || [];
  const videos = library?.videos || [];
  const searchTokens = useMemo(() => getSearchTokens(query), [query]);

  const topicNamesByVideo = useMemo(() => {
    const map = new Map();
    topics.forEach(topic => {
      topic.items.forEach(item => {
        const current = map.get(item.video.id) || [];
        if (!current.includes(topic.name)) current.push(topic.name);
        map.set(item.video.id, current);
      });
    });
    return map;
  }, [topics]);

  const activeTopic = activeTopicId === 'all'
    ? null
    : topics.find(topic => topic.id === activeTopicId) || null;

  const filteredVideos = useMemo(() => {
    const baseVideos = activeTopic
      ? activeTopic.items.map(item => item.video)
      : videos;
    const uniqueVideos = Array.from(new Map(baseVideos.map(video => [video.id, video])).values());

    if (searchTokens.length === 0) return uniqueVideos;

    return uniqueVideos
      .map(video => ({
        video,
        score: scoreVideo(video, query, searchTokens, topicNamesByVideo.get(video.id) || [])
      }))
      .filter(item => item.score > 0 && includesEveryToken(
        getVideoSearchText(item.video, topicNamesByVideo.get(item.video.id) || []),
        searchTokens
      ))
      .sort((a, b) => b.score - a.score || a.video.sort_order - b.video.sort_order)
      .map(item => item.video);
  }, [activeTopic, videos, query, searchTokens, topicNamesByVideo]);

  const matchingTopics = useMemo(() => {
    if (searchTokens.length === 0) return topics;
    return topics.filter(topic => {
      const topicText = [topic.name, topic.description, ...(topic.tags || [])].join(' ');
      if (includesEveryToken(topicText, searchTokens)) return true;
      return topic.items.some(item => includesEveryToken(
        getVideoSearchText(item.video, [topic.name]),
        searchTokens
      ));
    });
  }, [topics, searchTokens]);

  if (status !== 'authorized') {
    const isFailure = status === 'denied' || status === 'error';

    return (
      <div className="min-h-screen bg-[var(--color-background)] text-[var(--color-text)] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg bg-[var(--color-surface)] border border-slate-200 rounded-3xl p-8 shadow-2xl">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 ${isFailure ? 'bg-slate-100' : 'bg-sky-100'}`}>
            <SafeIcon icon={isFailure ? FiLock : FiShield} className={`w-7 h-7 ${isFailure ? 'text-slate-600' : 'text-sky-600'}`} />
          </div>
          <h1 className="text-2xl font-bold mb-3">{isFailure ? 'Secure access required' : 'Securing your Training Centre'}</h1>
          <p className="text-slate-600 leading-relaxed">{message}</p>
          <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-600">
            <div className="flex items-start gap-3">
              <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
              <p>A copied or bookmarked Training Library address contains no training data or credentials. The signed-in application must supply an authorised library payload from its trusted backend.</p>
            </div>
          </div>
          {isFailure && (
            <button type="button" onClick={() => window.close()} className="mt-6 w-full px-4 py-3 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-medium transition-colors">Close and return to the application</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)] text-[var(--color-text)]">
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur-sm sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-sky-600">BPA Solutions Training</p>
            <h1 className="text-xl sm:text-2xl font-bold truncate mt-1">{appName}</h1>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-sky-50 border border-sky-100 text-slate-700 text-xs font-semibold whitespace-nowrap">
            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-sky-600" /> Secure app session
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <section className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 shadow-sm overflow-hidden relative">
          <div className="absolute right-0 top-0 w-56 h-56 opacity-40 pointer-events-none" aria-hidden="true" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, var(--color-accent) 1px, transparent 0)', backgroundSize: '18px 18px' }} />
          <div className="relative max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-50 border border-sky-100 text-sky-700 text-xs font-semibold">
              <SafeIcon icon={FiLayers} className="w-4 h-4" /> {topics.length} Topic{topics.length === 1 ? '' : 's'} · {videos.length} Video{videos.length === 1 ? '' : 's'}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-5">Training Centre</h2>
            <p className="text-slate-600 mt-3 leading-7">Find the right tutorial by Topic or describe what you need help with. Search is limited to the training content authorised for your current app session.</p>

            <div className="relative mt-6 max-w-2xl">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="What do you need help with?"
                className="w-full pl-12 pr-12 py-3.5 rounded-2xl bg-white border border-slate-300 text-sm text-slate-900 placeholder-slate-500 focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none shadow-sm"
                aria-label="Search training videos"
              />
              {query && (
                <button type="button" onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-800" aria-label="Clear search">
                  <SafeIcon icon={FiX} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </section>

        {topics.length > 0 && (
          <section className="mt-8">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div><h3 className="text-lg font-bold">Browse by Topic</h3><p className="text-sm text-slate-500 mt-1">Topics group related tutorials into clear areas of work.</p></div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              <button type="button" onClick={() => setActiveTopicId('all')} className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors ${activeTopicId === 'all' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>All Topics</button>
              {topics.map(topic => (
                <button key={topic.id} type="button" onClick={() => setActiveTopicId(topic.id)} className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap border transition-colors ${activeTopicId === topic.id ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'}`}>{topic.name} <span className="opacity-70">({topic.items.length})</span></button>
              ))}
            </div>
          </section>
        )}

        {query.trim() && (
          <section className="mt-8">
            <div className="flex items-end justify-between gap-4 mb-5">
              <div><p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-600">Search results</p><h3 className="text-2xl font-bold mt-1">{filteredVideos.length} matching video{filteredVideos.length === 1 ? '' : 's'}</h3></div>
              {matchingTopics.length > 0 && <p className="text-sm text-slate-500">Across {matchingTopics.length} matching Topic{matchingTopics.length === 1 ? '' : 's'}</p>}
            </div>
            {filteredVideos.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredVideos.map(video => <VideoCard key={video.id} video={video} topicNames={topicNamesByVideo.get(video.id) || []} />)}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center"><SafeIcon icon={FiSearch} className="w-10 h-10 text-slate-300 mx-auto" /><h4 className="font-bold text-lg mt-4">No matching tutorials</h4><p className="text-slate-500 mt-2 text-sm">Try fewer words, a Topic name, a feature name or a common task.</p></div>
            )}
          </section>
        )}

        {!query.trim() && activeTopic && (
          <section className="mt-8">
            <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-7">
              <div className="mb-6"><p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-600">Topic</p><h3 className="text-2xl font-bold mt-1">{activeTopic.name}</h3>{activeTopic.description && <p className="text-slate-600 mt-2 leading-6 max-w-3xl">{activeTopic.description}</p>}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {activeTopic.items.map(item => <VideoCard key={item.id} video={item.video} topicNames={[activeTopic.name]} />)}
              </div>
            </div>
          </section>
        )}

        {!query.trim() && !activeTopic && topics.length > 0 && (
          <section className="mt-9 space-y-8">
            {topics.map(topic => (
              <div key={topic.id} className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-7 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
                  <div><p className="text-xs font-semibold uppercase tracking-[0.16em] text-sky-600">Topic</p><h3 className="text-2xl font-bold mt-1">{topic.name}</h3>{topic.description && <p className="text-slate-600 mt-2 text-sm leading-6 max-w-3xl">{topic.description}</p>}</div>
                  <span className="text-xs font-semibold text-slate-500">{topic.items.length} video{topic.items.length === 1 ? '' : 's'}</span>
                </div>
                {topic.items.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {topic.items.map(item => <VideoCard key={item.id} video={item.video} topicNames={[topic.name]} />)}
                  </div>
                ) : <p className="text-sm text-slate-500">No videos are available in this Topic yet.</p>}
              </div>
            ))}
          </section>
        )}

        {!query.trim() && videos.length > 0 && (
          <section className="mt-10">
            <div className="flex items-center gap-2 mb-5"><SafeIcon icon={FiPlay} className="w-5 h-5 text-sky-600" /><h3 className="text-xl font-bold">All Training Videos</h3></div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {videos.map(video => <VideoCard key={video.id} video={video} topicNames={topicNamesByVideo.get(video.id) || []} />)}
            </div>
          </section>
        )}

        {videos.length === 0 && (
          <section className="mt-8 bg-white border border-slate-200 rounded-3xl p-10 text-center">
            <SafeIcon icon={FiVideo} className="w-12 h-12 text-slate-300 mx-auto" />
            <h4 className="font-bold text-lg mt-4">No training videos are available yet</h4>
            <p className="text-slate-500 mt-2 text-sm">Published training content for this application will appear here automatically.</p>
          </section>
        )}
      </main>
    </div>
  );
};

const VideoCard = ({ video, topicNames = [] }) => {
  if (!video) return null;
  const duration = formatDuration(video.duration_seconds);

  const openVideo = () => {
    if (!isSafeHttpUrl(video.video_url)) return;
    window.open(video.video_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <article className="group bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex flex-col min-h-full">
      <div className="aspect-video bg-slate-100 relative overflow-hidden">
        {video.thumbnail_url ? (
          <img src={video.thumbnail_url} alt={`${video.title} thumbnail`} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" loading="lazy" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300"><SafeIcon icon={FiVideo} className="w-12 h-12" /></div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <span className="w-12 h-12 rounded-full bg-white/95 text-slate-800 flex items-center justify-center shadow-xl"><SafeIcon icon={FiPlay} className="w-5 h-5 ml-0.5" /></span>
        </div>
        {duration && <span className="absolute right-2 bottom-2 px-2 py-1 rounded-lg bg-slate-900/85 text-white text-[10px] font-semibold flex items-center gap-1"><SafeIcon icon={FiClock} className="w-3 h-3" />{duration}</span>}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="flex flex-wrap gap-2 mb-3">
          {video.category && <span className="px-2.5 py-1 rounded-full bg-sky-50 border border-sky-100 text-sky-700 text-[10px] font-semibold uppercase tracking-wider">{video.category.replaceAll('_', ' ')}</span>}
          {topicNames.slice(0, 1).map(topic => <span key={topic} className="px-2.5 py-1 rounded-full bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-semibold">{topic}</span>)}
        </div>
        <h4 className="font-bold text-lg leading-snug text-slate-900">{video.title}</h4>
        <p className="text-slate-600 text-sm mt-2 leading-6 line-clamp-3 min-h-[72px]">{video.description || 'Open this tutorial for step-by-step guidance.'}</p>
        <div className="mt-auto pt-5">
          <button type="button" onClick={openVideo} disabled={!video.video_url} className="w-full px-4 py-2.5 rounded-xl bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2 transition-colors">
            <SafeIcon icon={FiExternalLink} className="w-4 h-4" /> Watch tutorial
          </button>
        </div>
      </div>
    </article>
  );
};

export default TrainingLibrary;